import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useDeviceModels } from '@/hooks/useCatalog';
import { useDeleteDeviceModel } from '@/hooks/useCatalogActions';
import type { DeviceModel } from '@octave/contracts';
import { cn } from '@/lib/utils';
import { ChevronRight, Pencil, Plus, Smartphone, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DeviceModelForm } from './DeviceModelForm';

export function DeviceModelsView() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    model: DeviceModel | null;
  }>({ open: false, model: null });
  const navigate = useNavigate();

  const { data: deviceModels, isLoading } = useDeviceModels();
  const { mutate: deleteDeviceModel, isPending: isDeleting } =
    useDeleteDeviceModel();

  const handleEdit = (model: DeviceModel) => {
    navigate(`/o/admin/device-models/${model.id}`);
  };

  const handleDelete = () => {
    if (!deleteDialog.model) return;
    deleteDeviceModel(deleteDialog.model.id, {
      onSuccess: () => setDeleteDialog({ open: false, model: null }),
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(6)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div />
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="bg-white border-slate-200 text-slate-600 gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Nouveau modèle
            </Button>
          </DialogTrigger>
          <DialogContent className="!max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-slate-900">
                Nouveau modèle
              </DialogTitle>
            </DialogHeader>
            <DeviceModelForm
              model={null}
              onSuccess={() => setIsFormOpen(false)}
              onCancel={() => setIsFormOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {/* Card-based rows */}
      <div className="space-y-2">
        {deviceModels && deviceModels.length > 0 ? (
          deviceModels.map((model) => (
            <div
              key={model.id}
              onClick={() => handleEdit(model)}
              className="group flex items-center gap-4 bg-white rounded-xl px-5 py-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.005] active:scale-[0.995] border border-transparent hover:border-emerald-100"
            >
              {/* Image / Icon */}
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 group-hover:bg-emerald-50 transition-colors overflow-hidden">
                {model.imageUrl ? (
                  <img
                    src={model.imageUrl}
                    alt={model.name}
                    className="h-8 w-8 object-contain"
                  />
                ) : (
                  <Smartphone className="h-4 w-4 text-slate-400 group-hover:text-emerald-600" />
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-x-6 items-center">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {model.brand} {model.name}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700">
                      {model.category}
                    </span>
                    <span className="text-xs text-slate-400">
                      {model.variants?.length || 0} déclinaison
                      {(model.variants?.length || 0) > 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {/* Status */}
                <div className="hidden sm:block">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
                      model.isActive
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'bg-slate-100 text-slate-500'
                    )}
                  >
                    <span
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        model.isActive ? 'bg-emerald-500' : 'bg-slate-400'
                      )}
                    />
                    {model.isActive ? 'Actif' : 'Inactif'}
                  </span>
                </div>

                {/* Actions */}
                <div className="hidden sm:flex items-center gap-1">
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(model);
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Modifier</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteDialog({ open: true, model });
                          }}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Supprimer</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>

              <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500 shrink-0 transition-colors" />
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-slate-400">
            <Smartphone className="h-10 w-10 mb-3 text-slate-300" />
            <p className="text-sm font-medium">Aucun modèle</p>
            <p className="text-xs mt-1">
              Créez votre premier modèle d'appareil
            </p>
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, model: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer le modèle</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer{' '}
              <strong>
                {deleteDialog.model?.brand} {deleteDialog.model?.name}
              </strong>{' '}
              ? Cette action est irréversible et supprimera également toutes les
              déclinaisons associées.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialog({ open: false, model: null })}
              disabled={isDeleting}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
